'use client'

import { useEffect, useMemo, useState } from 'react'
import { addDays, parseISO, isAfter, isBefore, format } from 'date-fns'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useDebouncedCallback } from 'use-debounce'
import { useStmWeeksWithStats, useStmGroups } from '@/features/stm/hooks/use-stm-weeks'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
  Users,
  Calendar,
  AlertTriangle,
  Ban,
  Target,
  UserCheck,
  FileCheck,
  ClipboardList,
  ChevronRight,
  Phone,
  Info,
  Search,
  Filter,
  X,
} from 'lucide-react'
import { buildRegionUrl, cn } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import { formatDateRange, formatGroupDateRange } from '@/features/stm/lib/date-utils'
import type { WeekWithStats, Group, TripType } from '@/features/stm/lib/types'
import {
  SEARCH_PARAMS_TAB_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
} from '@/features/volunteers/constants/volunteer-tab-links'
import {
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { Skeleton } from '@/shared/components/ui/skeleton'

const CHART_COLORS = [
  '#3E7C59', // Reports green (completed)
  '#C9A961', // Reports gold (to go / not complete)
  '#003764', // ReliefHub navy
  '#AFC4D6', // ReliefHub soft blue
  '#5BA375', // Reports green variant
  '#2E8B57', // Reports goodwill complete
  '#7CB342', // Reports materials-only complete
  '#7E8A97', // Muted slate-blue neutral
]

const TRIP_TYPE_PIE_COLORS: Record<TripType, string> = {
  'Vision Trip': '#003764',
  'Skilled Labor': '#C9A961',
  'Volunteer': '#3E7C59',
  'Youth Trip': '#AFC4D6',
  'Adult Trip': '#5BA375',
  'Family Trip': '#2E8B57',
  'Semesters': '#7CB342',
  'Gap': '#7E8A97',
  'WR': '#8AA6B8',
  'Leadership': '#4B5563',
  'Partner': '#6B7280',
  'Other': '#9CA3AF',
}

function getTripTypePieColor(name: string): string {
  if (name in TRIP_TYPE_PIE_COLORS) {
    return TRIP_TYPE_PIE_COLORS[name as TripType]
  }
  // Deterministic fallback for unexpected categories from API data.
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  return CHART_COLORS[Math.abs(hash) % CHART_COLORS.length]
}

const tripTypeColors: Record<TripType, string> = {
  'Vision Trip': 'bg-blue-500',
  'Skilled Labor': 'bg-amber-500',
  'Volunteer': 'bg-emerald-500',
  'Youth Trip': 'bg-violet-500',
  'Adult Trip': 'bg-indigo-500',
  'Family Trip': 'bg-pink-500',
  'Semesters': 'bg-cyan-500',
  'Gap': 'bg-teal-500',
  'WR': 'bg-orange-500',
  'Leadership': 'bg-slate-500',
  'Partner': 'bg-violet-500',
  'Other': 'bg-gray-400',
}

const retentionBadgeColors: Record<'Return' | 'New', string> = {
  Return: 'bg-[#3E7C59]',
  New: 'bg-[#C9A961]',
}

type RevenueCategory = 'STM Revenue' | 'AR Revenue' | 'No Revenue'

const retentionPieColors: Record<'Return' | 'New', string> = {
  Return: '#3E7C59',
  New: '#C9A961',
}

const revenuePieColors: Record<RevenueCategory, string> = {
  'STM Revenue': '#3E7C59',
  'AR Revenue': '#003764',
  'No Revenue': '#C9A961',
}

const revenueCategoryTooltips: Record<RevenueCategory, string> = {
  'STM Revenue':
    "Counts volunteers for current-year, non-cancelled groups where Project ID contains 'Y', 'E', or 'F', unless the same ID also matches AR Revenue rules or No Revenue overrides.",
  'AR Revenue':
    "Counts volunteers for current-year, non-cancelled groups where Project ID contains 'D', 'AR', 'P', or 'R', except explicit No Revenue overrides.",
  'No Revenue':
    "Counts volunteers for current-year, non-cancelled groups where Project ID is blank/NA/None/No, is ARVOL/AR-VOL, contains 'V', or does not match STM Revenue/AR Revenue rules (all remaining IDs).",
}

const revenueTitleClassName =
  'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-cyan-600 text-white flex-shrink-0'

/** Trip Type Distribution — desktop donuts: match Recharts default label look (muted gray, regular weight). */
const DESKTOP_PIE_LABEL_FONT_SIZE = 12
const DESKTOP_PIE_LABEL_FILL = 'hsl(var(--muted-foreground))'

function desktopPieSingleLineLabel(props: {
  cx: number
  x?: number
  y?: number
  name: string
  percent: number
}) {
  const { cx, x = 0, y = 0, name, percent } = props
  const pct = (percent * 100).toFixed(0)
  const textAnchor = x >= cx ? 'start' : 'end'
  return (
    <text
      x={x}
      y={y}
      fontSize={DESKTOP_PIE_LABEL_FONT_SIZE}
      fontWeight={400}
      fill={DESKTOP_PIE_LABEL_FILL}
      textAnchor={textAnchor}
      dominantBaseline="middle"
    >
      {`${name} (${pct}%)`}
    </text>
  )
}

/** Two-line labels (name / percent) for mobile donuts — retention, top trip types, etc. */
function mobileTwoLinePieLabel(
  name: string,
  percent: number,
  x: number,
  y: number,
  cx: number
) {
  const pct = (percent * 100).toFixed(0)
  const textAnchor = x >= cx ? 'start' : 'end'
  return (
    <text x={x} y={y} fontSize={10} fontWeight={600} textAnchor={textAnchor} dominantBaseline="middle">
      <tspan x={x} dy="-0.55em" fill="hsl(var(--foreground))">
        {name}
      </tspan>
      <tspan x={x} dy="1.15em" fill="hsl(var(--muted-foreground))">
        {`(${pct}%)`}
      </tspan>
    </text>
  )
}

function mobileRevenueLabelShort(name: string): string {
  if (name === 'STM Revenue') return 'STM Rev.'
  if (name === 'AR Revenue') return 'AR Rev.'
  if (name === 'No Revenue') return 'No rev.'
  return name
}

/** Desktop revenue donut (3rd chart): two-line labels so "STM Revenue" is not clipped at the left edge. */
function desktopRevenuePieLabel(
  props: {
    cx: number
    x?: number
    y?: number
    name: string
    percent: number
  }
) {
  const { cx, x = 0, y = 0, name, percent } = props
  const pct = (percent * 100).toFixed(0)
  const textAnchor = x >= cx ? 'start' : 'end'
  if (name === 'STM Revenue') {
    return (
      <text
        x={x}
        y={y}
        fontSize={DESKTOP_PIE_LABEL_FONT_SIZE}
        fontWeight={400}
        fill={DESKTOP_PIE_LABEL_FILL}
        textAnchor={textAnchor}
        dominantBaseline="middle"
      >
        <tspan x={x} dy="-0.6em" fill={DESKTOP_PIE_LABEL_FILL}>
          STM
        </tspan>
        <tspan x={x} dy="1.2em" fill={DESKTOP_PIE_LABEL_FILL}>
          {`Revenue (${pct}%)`}
        </tspan>
      </text>
    )
  }
  return (
    <text
      x={x}
      y={y}
      fontSize={DESKTOP_PIE_LABEL_FONT_SIZE}
      fontWeight={400}
      fill={DESKTOP_PIE_LABEL_FILL}
      textAnchor={textAnchor}
      dominantBaseline="middle"
    >
      <tspan x={x} dy="-0.55em" fill={DESKTOP_PIE_LABEL_FILL}>
        {name}
      </tspan>
      <tspan x={x} dy="1.15em" fill={DESKTOP_PIE_LABEL_FILL}>
        {`(${pct}%)`}
      </tspan>
    </text>
  )
}

/** Two-line labels for revenue slices (short title / percent); same layout as retention mobile. */
function mobileRevenuePieLabel(
  name: string,
  percent: number,
  x: number,
  y: number,
  cx: number
) {
  const pct = (percent * 100).toFixed(0)
  const textAnchor = x >= cx ? 'start' : 'end'
  const line1 = mobileRevenueLabelShort(name)
  return (
    <text x={x} y={y} fontSize={10} fontWeight={600} textAnchor={textAnchor} dominantBaseline="middle">
      <tspan x={x} dy="-0.55em" fill="hsl(var(--foreground))">
        {line1}
      </tspan>
      <tspan x={x} dy="1.15em" fill="hsl(var(--muted-foreground))">
        {`(${pct}%)`}
      </tspan>
    </text>
  )
}

function categorizeProjectRevenue(projectId: string | null | undefined): RevenueCategory {
  const normalizedText = (projectId ?? '').trim().toLowerCase()
  if (normalizedText === '' || normalizedText === 'na' || normalizedText === 'none' || normalizedText === 'no') {
    return 'No Revenue'
  }

  const normalized = (projectId ?? '').trim().toUpperCase()
  if (normalized === 'ARVOL' || normalized === 'AR-VOL') {
    return 'No Revenue'
  }

  // AR Revenue has highest precedence when IDs overlap categories.
  if (normalized.includes('AR') || normalized.includes('D') || normalized.includes('P') || normalized.includes('R')) {
    return 'AR Revenue'
  }

  // STM Revenue buckets by Y/E/F project IDs.
  if (normalized.includes('Y') || normalized.includes('E') || normalized.includes('F')) {
    return 'STM Revenue'
  }

  // No Revenue includes V and any uncategorized project IDs.
  return 'No Revenue'
}

function groupTripStartDate(g: Group): Date | null {
  const s = g.start_date
  if (s == null || String(s).trim() === '') return null
  const d = parseISO(s)
  return isNaN(d.getTime()) ? null : d
}

function groupMatchesDashboardSearch(group: Group, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true
  const dateRange = formatGroupDateRange(group.start_date, group.end_date)
  const revenueCategory = categorizeProjectRevenue(group.project_id)
  return [
    group.group_name,
    group.church_org,
    group.project_id,
    group.trip_type,
    group.status,
    group.retention_group,
    group.contact_name,
    group.contact_email,
    group.contact_phone,
    group.leader_name,
    group.leader_email,
    group.leader_phone,
    group.notes,
    dateRange,
    revenueCategory,
  ].some((value) => value?.toLowerCase().includes(normalizedQuery))
}

interface ClickableGroupItemProps {
  group: Group
  onClick: () => void
  showDetails?: boolean
}

function ClickableGroupItem({
  group,
  onClick,
  showDetails = false,
}: ClickableGroupItemProps) {
  const statusStyles: Record<string, string> = {
    Confirmed: 'border-l-emerald-500',
    Tentative: 'border-l-amber-500 border-dashed',
    Cancelled: 'border-l-slate-400 opacity-50',
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-3 rounded-lg border border-border/60 bg-white/90 dark:bg-slate-800/90 border-l-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        statusStyles[group.status],
        group.status === 'Cancelled' && 'line-through'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {group.project_id && (
              <span className="text-sm font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                {group.project_id}
              </span>
            )}
            <span className="font-semibold text-sm truncate">{group.group_name}</span>
            <span
              className={cn(
                'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded text-white flex-shrink-0',
                group.trip_type ? tripTypeColors[group.trip_type] : 'bg-zinc-500'
              )}
            >
              {group.trip_type ?? 'Not set'}
            </span>
          </div>
          {showDetails && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mt-2">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Users className="h-3 w-3" />
                <span className="font-medium text-foreground">{group.participants}</span> participants
              </div>
              {group.contact_name && (
                <div className="flex items-center gap-1 text-muted-foreground col-span-2">
                  <Phone className="h-3 w-3" />
                  <span className="truncate">{group.contact_name}</span>
                  {group.contact_phone && (
                    <span className="text-muted-foreground/70"> • {group.contact_phone}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 text-muted-foreground col-span-2">
                <Calendar className="h-3 w-3" />
                <span>{formatGroupDateRange(group.start_date, group.end_date)}</span>
              </div>
            </div>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
      </div>
    </button>
  )
}

interface DashboardStatsProps {
  onNavigateToGroups?: () => void
  onNavigateToGroup?: (groupId: string) => void
  onNavigateToCalendarWeek?: (weekStart: string) => void
  regionId?: number | undefined | null
}

function CardCalcIcon({ text, className }: { text: string; className: string }) {
  return (
    <Popover>
      <TooltipProvider delayDuration={100}>
        <UiTooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground',
                  className
                )}
                aria-label="How this is calculated"
              >
                <Info className="h-3 w-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[280px] text-xs leading-snug">
            {text}
          </TooltipContent>
        </UiTooltip>
      </TooltipProvider>
      <PopoverContent side="bottom" className="max-w-[320px] text-sm leading-snug">
        {text}
      </PopoverContent>
    </Popover>
  )
}

export function DashboardStats({
  onNavigateToGroups,
  onNavigateToGroup,
  onNavigateToCalendarWeek,
  regionId,
}: DashboardStatsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const now = new Date()
  const currentMonthStart = format(now, 'yyyy-MM-01')
  const monthsToEndOfYear = 12 - now.getMonth()
  const { data: weeks, isLoading: weeksLoading } = useStmWeeksWithStats(
    regionId ?? null,
    currentMonthStart,
    monthsToEndOfYear
  )
  const { data: groups, isLoading: groupsLoading } = useStmGroups(regionId ?? null)

  const isLoading = weeksLoading || groupsLoading

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '')
  }, [searchParams])

  const updateSearchParam = useDebouncedCallback((value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value && value.trim().length > 0) {
      params.set('search', value)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }, 750)

  const handleOpenVolunteerSearch = () => {
    const search = searchQuery.trim()
    if (!search) return
    updateSearchParam.cancel()
    const params = {
      [SEARCH_PARAMS_TAB_KEY]: VOLUNTEER_TAB_FILTER_SEARCH,
      page: '1',
      search,
    }
    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/volunteers', params)
      : `/volunteers?${new URLSearchParams(params).toString()}`
    router.push(href)
  }

  const clearSearchParam = () => {
    updateSearchParam.cancel()
    setSearchQuery('')
    const params = new URLSearchParams(searchParams.toString())
    params.delete('search')
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const dashboardGroups = useMemo(
    () => groups?.filter((group) => groupMatchesDashboardSearch(group, normalizedSearchQuery)),
    [groups, normalizedSearchQuery]
  )

  const stats = useMemo(() => {
    if (!weeks || !dashboardGroups) return null
    const today = new Date()
    const next90Days = addDays(today, 90)
    const currentYear = today.getFullYear()
    const startOfYear = new Date(currentYear, 0, 1)
    const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59, 999)
    const weekWindowStart = addDays(today, -7)

    const upcomingWeeks = weeks.filter((w) => {
      const weekStart = parseISO(w.week_start)
      return isAfter(weekStart, addDays(today, -7)) && isBefore(weekStart, next90Days)
    })

    const weeksToEndOfYear = weeks.filter((w) => {
      const weekStart = parseISO(w.week_start)
      return weekStart >= weekWindowStart && weekStart <= endOfYear
    })

    const upcomingGroups = dashboardGroups.filter((g) => {
      if (g.status === 'Cancelled') return false
      const start = groupTripStartDate(g)
      if (!start) return true
      return isAfter(start, addDays(today, -7)) && isBefore(start, next90Days)
    })

    const nonCancelledCurrentYearGroups = dashboardGroups.filter((g) => {
      const start = groupTripStartDate(g)
      if (!start) return false
      return g.status !== 'Cancelled' && start >= startOfYear && start <= endOfYear
    })
    const sortedCurrentYearGroups = [...nonCancelledCurrentYearGroups].sort((a, b) => {
      const ta = groupTripStartDate(a)?.getTime() ?? 0
      const tb = groupTripStartDate(b)?.getTime() ?? 0
      const startCompare = ta - tb
      if (startCompare !== 0) return startCompare
      return Number(a.id) - Number(b.id)
    })
    const ytdGroups = sortedCurrentYearGroups.filter((g) => {
      const st = groupTripStartDate(g)
      return st != null && st <= today
    })
    const eoyGroups = sortedCurrentYearGroups.filter((g) => {
      const st = groupTripStartDate(g)
      return st != null && st >= today
    })
    const totalVolunteersCurrentYear = nonCancelledCurrentYearGroups.reduce((s, g) => s + g.participants, 0)
    const totalVolunteersYtd = ytdGroups.reduce((s, g) => s + g.participants, 0)
    const totalVolunteersDateToEndOfYear = eoyGroups.reduce((s, g) => s + g.participants, 0)
    const confirmedGroups = nonCancelledCurrentYearGroups.filter((g) => g.status === 'Confirmed')
    const overCapacityWeeks = upcomingWeeks.filter((w) => w.fillRate > 100)

    const tripTypeData: Record<string, number> = {}
    nonCancelledCurrentYearGroups.forEach((g) => {
      const ttKey = g.trip_type ?? 'Not set'
      tripTypeData[ttKey] = (tripTypeData[ttKey] || 0) + g.participants
    })
    const retentionData = {
      Return: nonCancelledCurrentYearGroups
        .filter((g) => g.retention_group === 'Return' || g.retention_group === 'Retention')
        .reduce((sum, g) => sum + g.participants, 0),
      New: nonCancelledCurrentYearGroups
        .filter((g) => g.retention_group === 'New')
        .reduce((sum, g) => sum + g.participants, 0),
    }
    const revenueData: Record<RevenueCategory, number> = {
      'STM Revenue': 0,
      'AR Revenue': 0,
      'No Revenue': 0,
    }
    const revenueGroupsByCategory: Record<RevenueCategory, Group[]> = {
      'STM Revenue': [],
      'AR Revenue': [],
      'No Revenue': [],
    }
    nonCancelledCurrentYearGroups.forEach((g) => {
      const category = categorizeProjectRevenue(g.project_id)
      revenueData[category] += g.participants
      revenueGroupsByCategory[category].push(g)
    })

    const needsLeader = upcomingGroups.filter((g) => !g.leader_assigned)
    const needsPretrip = upcomingGroups.filter((g) => !g.pretrip_info_given)

    return {
      totalVolunteersCurrentYear,
      totalVolunteersYtd,
      totalVolunteersDateToEndOfYear,
      totalGroups: nonCancelledCurrentYearGroups.length,
      confirmedGroups: confirmedGroups.length,
      overCapacityWeeks,
      next12WeeksData: weeksToEndOfYear,
      tripTypeData: Object.entries(tripTypeData).map(([name, value]) => ({ name, value })),
      retentionData,
      revenueData,
      revenueGroupsByCategory,
      revenuePieData: [
        { name: 'STM Revenue', value: revenueData['STM Revenue'] },
        { name: 'AR Revenue', value: revenueData['AR Revenue'] },
        { name: 'No Revenue', value: revenueData['No Revenue'] },
      ],
      needsLeader,
      needsPretrip,
      currentYearGroups: sortedCurrentYearGroups,
      ytdGroups,
      eoyGroups,
    }
  }, [weeks, dashboardGroups])

  /** Top 3 trip types by participants — only these get slice labels on mobile trip chart */
  const topThreeTripTypeNames = useMemo(() => {
    const rows = stats?.tripTypeData
    if (!rows?.length) return new Set<string>()
    return new Set(
      [...rows]
        .sort((a, b) => {
          if (b.value !== a.value) return b.value - a.value
          return a.name.localeCompare(b.name)
        })
        .slice(0, 3)
        .map((r) => r.name)
    )
  }, [stats?.tripTypeData])

  const handleGroupClick = (g: Group) => {
    onNavigateToGroup?.(String(g.id))
  }

  const canNavigateToGroup = typeof onNavigateToGroup === 'function'
  const canNavigateToGroups = typeof onNavigateToGroups === 'function'

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-[400px] rounded-xl" />
      </div>
    )
  }

  if (!stats) return <p className="text-muted-foreground">No data available</p>
  const retentionPieData = [
    { name: 'New', value: stats.retentionData.New },
    { name: 'Return', value: stats.retentionData.Return },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="relative flex-1 min-w-[220px] sm:min-w-[360px] lg:min-w-[520px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search volunteers, groups, dates..."
            value={searchQuery}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value
              setSearchQuery(value)
              updateSearchParam(value)
            }}
            className="pl-8 sm:pl-9 h-8 sm:h-10 text-sm"
          />
        </div>
        {searchQuery.trim().length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenVolunteerSearch}
            className="h-8 sm:h-10 shrink-0 text-xs sm:text-sm whitespace-nowrap"
          >
            Search volunteers
          </Button>
        )}
      </div>

      {searchQuery.trim().length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm text-muted-foreground">
            <span className="font-bold">
              Showing {(dashboardGroups?.length ?? 0).toLocaleString()} result{(dashboardGroups?.length ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Active filters:</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
              <span className="font-medium">Search:</span>
              <span>{searchQuery.trim()}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-primary/20"
                onClick={clearSearchParam}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSearchParam}
              className="text-xs"
            >
              Clear all
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="relative overflow-hidden border-2 border-[#3E7C59]/25 bg-[#E8F3EE] dark:bg-[#1F3A2D]/55">
          <CardHeader className="flex flex-row items-center justify-between pt-3 pb-0 relative">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Total Groups {new Date().getFullYear()}
              <CardCalcIcon
                className="text-[#3E7C59] hover:text-[#2E5D43] dark:text-[#5BA375] dark:hover:text-[#7CB342]"
                text="Count of current-year groups where status is Tentative or Confirmed. Cancelled groups are excluded."
              />
            </CardTitle>
            <div className="p-2 rounded-lg bg-[#3E7C59]/15">
              <Calendar className="h-4 w-4 text-[#3E7C59] dark:text-[#5BA375]" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-1 pb-2">
            <div className="text-2xl font-bold text-foreground">
              {stats.totalGroups}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-foreground font-medium">
                {stats.confirmedGroups}
              </span>{' '}
              confirmed
            </p>
            <details className="group mt-2">
              <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Show groups ({stats.currentYearGroups.length})
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-muted bg-muted/20 p-2 text-[11px] dark:bg-slate-900/40">
                {stats.currentYearGroups.map((group) => (
                  canNavigateToGroup || canNavigateToGroups ? (
                    <button
                      key={`current-year-${group.id}`}
                      type="button"
                      onClick={() => {
                        if (canNavigateToGroup) {
                          onNavigateToGroup?.(group.id)
                          return
                        }
                        onNavigateToGroups?.()
                      }}
                      className="block w-full py-0.5 text-left text-muted-foreground hover:text-primary hover:underline"
                    >
                      {group.group_name}
                      {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants}
                    </button>
                  ) : (
                    <div key={`current-year-${group.id}`} className="py-0.5 text-muted-foreground">
                      {group.group_name}
                      {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants}
                    </div>
                  )
                ))}
              </div>
            </details>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-[#3E7C59]/25 bg-[#E8F3EE] dark:bg-[#1F3A2D]/55">
          <CardHeader className="flex flex-row items-center justify-between pt-3 pb-0 relative">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Total Volunteers {new Date().getFullYear()}
              <CardCalcIcon
                className="text-[#3E7C59] hover:text-[#2E5D43] dark:text-[#5BA375] dark:hover:text-[#7CB342]"
                text="Sum of volunteers for all current-year groups where status is Tentative or Confirmed. Cancelled groups are excluded."
              />
            </CardTitle>
            <div className="p-2 rounded-lg bg-[#3E7C59]/15">
              <Users className="h-4 w-4 text-[#3E7C59] dark:text-[#5BA375]" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-1 pb-2">
            <div className="text-2xl font-bold text-foreground">
              {stats.totalVolunteersCurrentYear}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{new Date().getFullYear()} scheduled volunteers</p>
            <details className="group mt-2">
              <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Show by-group breakdown ({stats.currentYearGroups.length})
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-muted bg-muted/20 p-2 text-[11px] dark:bg-slate-900/40">
                {(() => {
                  let running = 0
                  return stats.currentYearGroups.map((group) => {
                    running += group.participants
                    return (
                      canNavigateToGroup || canNavigateToGroups ? (
                        <button
                          key={`current-year-vols-${group.id}`}
                          type="button"
                          onClick={() => {
                            if (canNavigateToGroup) {
                              onNavigateToGroup?.(group.id)
                              return
                            }
                            onNavigateToGroups?.()
                          }}
                          className="block w-full py-0.5 text-left text-muted-foreground hover:text-primary hover:underline"
                        >
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </button>
                      ) : (
                        <div key={`current-year-vols-${group.id}`} className="py-0.5 text-muted-foreground">
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </div>
                      )
                    )
                  })
                })()}
              </div>
            </details>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-[#003764]/25 bg-[#EAF0F6] dark:bg-[#1E2E40]/55">
          <CardHeader className="flex flex-row items-center justify-between pt-3 pb-0 relative">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Total Volunteers to Date
              <CardCalcIcon
                className="text-[#003764] hover:text-[#0A4E83] dark:text-[#AFC4D6] dark:hover:text-white"
                text="Sum of volunteers for current-year groups with start date from Jan 1 through today, where status is Tentative or Confirmed. Cancelled groups are excluded."
              />
            </CardTitle>
            <div className="p-2 rounded-lg bg-[#003764]/15">
              <Users className="h-4 w-4 text-[#003764] dark:text-[#AFC4D6]" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-1 pb-2">
            <div className="text-2xl font-bold text-foreground">
              {stats.totalVolunteersYtd}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Jan 1 to today</p>
            <details className="group mt-2">
              <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Show by-group breakdown ({stats.ytdGroups.length})
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-muted bg-muted/20 p-2 text-[11px] dark:bg-slate-900/40">
                {(() => {
                  let running = 0
                  return stats.ytdGroups.map((group) => {
                    running += group.participants
                    return (
                      canNavigateToGroup || canNavigateToGroups ? (
                        <button
                          key={`ytd-vols-${group.id}`}
                          type="button"
                          onClick={() => {
                            if (canNavigateToGroup) {
                              onNavigateToGroup?.(group.id)
                              return
                            }
                            onNavigateToGroups?.()
                          }}
                          className="block w-full py-0.5 text-left text-muted-foreground hover:text-primary hover:underline"
                        >
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </button>
                      ) : (
                        <div key={`ytd-vols-${group.id}`} className="py-0.5 text-muted-foreground">
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </div>
                      )
                    )
                  })
                })()}
              </div>
            </details>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-[#003764]/25 bg-[#EAF0F6] dark:bg-[#1E2E40]/55">
          <CardHeader className="flex flex-row items-center justify-between pt-3 pb-0 relative">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Total Volunteers to End of Year
              <CardCalcIcon
                className="text-[#003764] hover:text-[#0A4E83] dark:text-[#AFC4D6] dark:hover:text-white"
                text="Includes all current-year groups from today through Dec 31 where status is Tentative or Confirmed. Cancelled groups are excluded."
              />
            </CardTitle>
            <div className="p-2 rounded-lg bg-[#003764]/15">
              <Users className="h-4 w-4 text-[#003764] dark:text-[#AFC4D6]" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-1 pb-2">
            <div className="text-2xl font-bold text-foreground">
              {stats.totalVolunteersDateToEndOfYear}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Today to Dec 31</p>
            <details className="group mt-2">
              <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Show by-group breakdown ({stats.eoyGroups.length})
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-muted bg-muted/20 p-2 text-[11px] dark:bg-slate-900/40">
                {(() => {
                  let running = 0
                  return stats.eoyGroups.map((group) => {
                    running += group.participants
                    return (
                      canNavigateToGroup || canNavigateToGroups ? (
                        <button
                          key={`eoy-vols-${group.id}`}
                          type="button"
                          onClick={() => {
                            if (canNavigateToGroup) {
                              onNavigateToGroup?.(group.id)
                              return
                            }
                            onNavigateToGroups?.()
                          }}
                          className="block w-full py-0.5 text-left text-muted-foreground hover:text-primary hover:underline"
                        >
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </button>
                      ) : (
                        <div key={`eoy-vols-${group.id}`} className="py-0.5 text-muted-foreground">
                          {group.group_name}
                          {group.status === 'Tentative' ? ' (Tentative)' : ''} - {group.participants} (running{' '}
                          {running})
                        </div>
                      )
                    )
                  })
                })()}
              </div>
            </details>
          </CardContent>
        </Card>

      </div>

      {/* Volunteers by Trip Type */}
      <Card className="border-2">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            Volunteers by Trip Type {new Date().getFullYear()}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {stats.tripTypeData.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">No volunteers scheduled</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {(Object.entries(stats.retentionData) as Array<['Return' | 'New', number]>)
                  .sort(([a], [b]) => (a === 'New' ? -1 : b === 'New' ? 1 : 0))
                  .map(([name, value]) => (
                    <div
                      key={name}
                      className="p-4 rounded-xl border bg-gradient-to-br from-white to-muted/30 dark:from-slate-900 dark:to-slate-800/50"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0 bg-slate-300" />
                        <span
                          className={cn(
                            'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-white flex-shrink-0',
                            retentionBadgeColors[name]
                          )}
                        >
                          {name}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-foreground">{value}</div>
                      <p className="text-xs text-muted-foreground">volunteers</p>
                    </div>
                  ))}
                {[...stats.tripTypeData]
                  .sort((a, b) => b.value - a.value)
                  .filter((item) => item.name === 'Skilled Labor')
                  .map((item, i) => (
                    <div
                      key={item.name}
                      className="p-4 rounded-xl border bg-gradient-to-br from-white to-muted/30 dark:from-slate-900 dark:to-slate-800/50"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getTripTypePieColor(item.name) }}
                        />
                        <span
                          className={cn(
                            'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-white flex-shrink-0',
                            tripTypeColors[item.name as TripType] || 'bg-gray-400'
                          )}
                        >
                          {item.name}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-foreground">{item.value}</div>
                      <p className="text-xs text-muted-foreground">volunteers</p>
                    </div>
                  ))}
                {[...stats.tripTypeData]
                  .sort((a, b) => b.value - a.value)
                  .filter((item) => item.name !== 'Skilled Labor')
                  .map((item, i) => (
                    <div
                      key={item.name}
                      className="p-4 rounded-xl border bg-gradient-to-br from-white to-muted/30 dark:from-slate-900 dark:to-slate-800/50"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getTripTypePieColor(item.name) }}
                        />
                        <span
                          className={cn(
                            'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-white flex-shrink-0',
                            tripTypeColors[item.name as TripType] || 'bg-gray-400'
                          )}
                        >
                          {item.name}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-foreground">{item.value}</div>
                      <p className="text-xs text-muted-foreground">volunteers</p>
                    </div>
                  ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(Object.entries(stats.revenueData) as Array<[RevenueCategory, number]>).map(([name, value]) => (
                  <div
                    key={name}
                    className="p-4 rounded-xl border bg-gradient-to-br from-white to-muted/30 dark:from-slate-900 dark:to-slate-800/50"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: revenuePieColors[name] }} />
                      <span className={revenueTitleClassName}>{name}</span>
                      <CardCalcIcon
                        className="text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200"
                        text={revenueCategoryTooltips[name]}
                      />
                    </div>
                    <div className="text-2xl font-bold text-foreground">{value}</div>
                    <p className="text-xs text-muted-foreground">volunteers</p>
                    <details className="group mt-2">
                      <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-200">
                        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                        Show by-group breakdown ({stats.revenueGroupsByCategory[name].length})
                      </summary>
                      <div className="mt-2 max-h-48 overflow-y-auto rounded border border-cyan-300/50 bg-white/70 p-2 text-[11px] dark:border-cyan-700/50 dark:bg-slate-900/40">
                        {(() => {
                          let running = 0
                          return stats.revenueGroupsByCategory[name].map((group) => {
                            running += group.participants
                            const rowText = `${group.group_name}${group.status === 'Tentative' ? ' (Tentative)' : ''} - ${group.participants} (running ${running})`
                            return canNavigateToGroup || canNavigateToGroups ? (
                              <button
                                key={`revenue-${name}-${group.id}`}
                                type="button"
                                onClick={() => {
                                  if (canNavigateToGroup) {
                                    onNavigateToGroup?.(group.id)
                                    return
                                  }
                                  onNavigateToGroups?.()
                                }}
                                className="block w-full py-0.5 text-left text-muted-foreground hover:text-primary hover:underline"
                              >
                                {rowText}
                              </button>
                            ) : (
                              <div key={`revenue-${name}-${group.id}`} className="py-0.5 text-muted-foreground">
                                {rowText}
                              </div>
                            )
                          })
                        })()}
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-6">
      <div className="order-1">
      {/* Charts */}
      <Card className="border-2">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Target className="h-5 w-5 text-primary" />
            </div>
            Trip Type Distribution
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-3">
            <div className="min-h-0 md:h-[270px]">
              {retentionPieData.every((item) => item.value === 0) ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No retention data available
                </div>
              ) : (
                <>
                  <div className="hidden h-full md:block">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={retentionPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={40}
                          label={(props) =>
                            desktopPieSingleLineLabel({
                              cx: Number(props.cx),
                              x: props.x != null ? Number(props.x) : undefined,
                              y: props.y != null ? Number(props.y) : undefined,
                              name: String(props.name),
                              percent: Number(props.percent),
                            })
                          }
                          labelLine={false}
                          strokeWidth={2}
                          stroke="hsl(var(--background))"
                        >
                          <Cell fill={retentionPieColors.New} />
                          <Cell fill={retentionPieColors.Return} />
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: '12px',
                            border: '1px solid hsl(var(--border))',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="md:hidden w-full min-w-0 px-1">
                    <div className="mx-auto h-[200px] min-h-[200px] w-full max-w-[300px] [&_.recharts-surface]:overflow-visible">
                      <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                        <PieChart margin={{ top: 10, right: 36, bottom: 10, left: 36 }}>
                          <Pie
                            data={retentionPieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={64}
                            innerRadius={28}
                            label={(props) =>
                              mobileTwoLinePieLabel(
                                String(props.name),
                                Number(props.percent),
                                Number(props.x),
                                Number(props.y),
                                Number(props.cx)
                              )
                            }
                            labelLine={false}
                            strokeWidth={2}
                            stroke="hsl(var(--background))"
                          >
                            <Cell fill={retentionPieColors.New} />
                            <Cell fill={retentionPieColors.Return} />
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid hsl(var(--border))',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="min-h-0 md:h-[270px]">
              {stats.tripTypeData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No trip data available
                </div>
              ) : (
                <>
                  <div className="hidden h-full md:block">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.tripTypeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={40}
                          label={(props) => {
                            const pct = Number(props.percent)
                            if (pct < 0.06) return null
                            return desktopPieSingleLineLabel({
                              cx: Number(props.cx),
                              x: props.x != null ? Number(props.x) : undefined,
                              y: props.y != null ? Number(props.y) : undefined,
                              name: String(props.name),
                              percent: pct,
                            })
                          }}
                          labelLine={false}
                          strokeWidth={2}
                          stroke="hsl(var(--background))"
                        >
                          {stats.tripTypeData.map((item) => (
                            <Cell key={`desktop-cell-${item.name}`} fill={getTripTypePieColor(item.name)} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: '12px',
                            border: '1px solid hsl(var(--border))',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="md:hidden w-full min-w-0 px-1">
                    <div className="mx-auto h-[200px] min-h-[200px] w-full max-w-[300px] [&_.recharts-surface]:overflow-visible">
                      <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                        <PieChart margin={{ top: 10, right: 36, bottom: 10, left: 36 }}>
                          <Pie
                            data={stats.tripTypeData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={64}
                            innerRadius={28}
                            label={(props) => {
                              const name = String(props.name)
                              if (!topThreeTripTypeNames.has(name)) return null
                              return mobileTwoLinePieLabel(
                                name,
                                Number(props.percent),
                                Number(props.x),
                                Number(props.y),
                                Number(props.cx)
                              )
                            }}
                            labelLine={false}
                            strokeWidth={2}
                            stroke="hsl(var(--background))"
                          >
                            {stats.tripTypeData.map((item) => (
                              <Cell key={`mobile-cell-${item.name}`} fill={getTripTypePieColor(item.name)} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid hsl(var(--border))',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="min-h-0 md:h-[270px]">
              {stats.revenuePieData.every((item) => item.value === 0) ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No revenue data available
                </div>
              ) : (
                <>
                  <div className="hidden h-full md:block [&_.recharts-surface]:overflow-visible">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 12, right: 12, bottom: 12, left: 20 }}>
                        <Pie
                          data={stats.revenuePieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={40}
                          label={(props) =>
                            desktopRevenuePieLabel({
                              cx: Number(props.cx),
                              x: props.x != null ? Number(props.x) : undefined,
                              y: props.y != null ? Number(props.y) : undefined,
                              name: String(props.name),
                              percent: Number(props.percent),
                            })
                          }
                          labelLine={false}
                          strokeWidth={2}
                          stroke="hsl(var(--background))"
                        >
                          {stats.revenuePieData.map((item) => (
                            <Cell
                              key={`desktop-revenue-cell-${item.name}`}
                              fill={revenuePieColors[item.name as RevenueCategory]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: '12px',
                            border: '1px solid hsl(var(--border))',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="md:hidden w-full min-w-0 px-1">
                    <div className="mx-auto h-[200px] min-h-[200px] w-full max-w-[300px] [&_.recharts-surface]:overflow-visible">
                      <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                        <PieChart margin={{ top: 10, right: 36, bottom: 10, left: 36 }}>
                          <Pie
                            data={stats.revenuePieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={64}
                            innerRadius={28}
                            label={(props) =>
                              mobileRevenuePieLabel(
                                String(props.name),
                                Number(props.percent),
                                Number(props.x),
                                Number(props.y),
                                Number(props.cx)
                              )
                            }
                            labelLine={false}
                            strokeWidth={2}
                            stroke="hsl(var(--background))"
                          >
                            {stats.revenuePieData.map((item) => (
                              <Cell
                                key={`mobile-revenue-cell-${item.name}`}
                                fill={revenuePieColors[item.name as RevenueCategory]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid hsl(var(--border))',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      <div className="order-2">
      {/* Groups Needing Attention */}
      {(stats.needsLeader.length > 0 || stats.needsPretrip.length > 0) && (
        <Card className="border-2 border-amber-500/30 bg-gradient-to-br from-amber-50/80 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/20">
          <CardHeader className="flex flex-row items-center gap-3 pb-3 border-b border-amber-200/50 dark:border-amber-800/30">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <ClipboardList className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-amber-700 dark:text-amber-300">
                Groups Needing Attention
              </CardTitle>
              <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-0.5">
                Click any group to edit
              </p>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border-2 border-amber-200/50 dark:border-amber-800/30 bg-white/80 dark:bg-slate-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/50">
                    <UserCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="text-sm font-semibold">Needs Leader Assigned</span>
                  <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    {stats.needsLeader.length}
                  </Badge>
                </div>
                {stats.needsLeader.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                    {stats.needsLeader.slice(0, 5).map((g) => (
                      <ClickableGroupItem
                        key={g.id}
                        group={g}
                        onClick={() => handleGroupClick(g)}
                        showDetails
                      />
                    ))}
                    {stats.needsLeader.length > 5 && onNavigateToGroups && (
                      <button
                        type="button"
                        onClick={onNavigateToGroups}
                        className="block w-full text-center text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 hover:underline py-2"
                      >
                        +{stats.needsLeader.length - 5} more groups →
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">All groups have leaders!</p>
                )}
              </div>

              <div className="p-4 rounded-xl border-2 border-amber-200/50 dark:border-amber-800/30 bg-white/80 dark:bg-slate-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/50">
                    <FileCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="text-sm font-semibold">Needs Pre-Trip Info</span>
                  <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    {stats.needsPretrip.length}
                  </Badge>
                </div>
                {stats.needsPretrip.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                    {stats.needsPretrip.slice(0, 5).map((g) => (
                      <ClickableGroupItem
                        key={g.id}
                        group={g}
                        onClick={() => handleGroupClick(g)}
                        showDetails
                      />
                    ))}
                    {stats.needsPretrip.length > 5 && onNavigateToGroups && (
                      <button
                        type="button"
                        onClick={onNavigateToGroups}
                        className="block w-full text-center text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 hover:underline py-2"
                      >
                        +{stats.needsPretrip.length - 5} more groups →
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">All groups have pre-trip info!</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      <div className="order-3">
      {/* Over Capacity Alerts */}
      {stats.overCapacityWeeks.length > 0 && (
        <Card className="border-2 border-rose-500/30 bg-gradient-to-br from-rose-50/80 to-red-50/50 dark:from-rose-950/30 dark:to-red-950/20">
          <CardHeader className="flex flex-row items-center gap-3 pb-3">
            <div className="p-2 rounded-lg bg-rose-500/20">
              <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            </div>
            <CardTitle className="text-lg text-rose-700 dark:text-rose-300">
              Over Capacity Alerts ({stats.overCapacityWeeks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.overCapacityWeeks.map((w) => (
                <Badge
                  key={w.id}
                  variant="destructive"
                  className="px-3 py-1.5 text-sm bg-rose-500 hover:bg-rose-600"
                >
                  {formatDateRange(w.week_start, w.week_end)} ({Math.round(w.fillRate)}%)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
      </div>

      {/* Next 12 Weeks Table */}
      <Card className="border-2">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            Upcoming Weeks Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3 py-4 md:hidden">
            {stats.next12WeeksData.map((w) => (
              <button
                key={`mobile-${w.id}`}
                type="button"
                onClick={() => onNavigateToCalendarWeek?.(w.week_start)}
                className={cn(
                  'w-full rounded-lg border bg-background p-3 text-left transition-colors',
                  onNavigateToCalendarWeek && 'cursor-pointer active:bg-muted/40'
                )}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold">{formatDateRange(w.week_start, w.week_end)}</div>
                  <div>
                    {w.hasHardBlock ? (
                      <Badge variant="secondary" className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                        <Ban className="h-3 w-3 mr-1" />
                        Blocked
                      </Badge>
                    ) : w.fillRate > 100 ? (
                      <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                        Over Capacity
                      </Badge>
                    ) : w.fillRate === 100 ? (
                      <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                        Full
                      </Badge>
                    ) : w.fillRate >= 90 ? (
                      <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                        Near Full
                      </Badge>
                    ) : w.fillRate >= 70 ? (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                        Filling
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                        Available
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="text-muted-foreground">Capacity</div>
                  <div className="text-right font-medium">{w.capacity}</div>
                  <div className="text-muted-foreground">Filled</div>
                  <div className="text-right font-medium">{w.filledSpots}</div>
                  <div className="text-muted-foreground">Available</div>
                  <div className="text-right font-medium">{w.availableSpots}</div>
                  <div className="text-muted-foreground">Fill Rate</div>
                  <div
                    className={cn(
                      'text-right font-semibold',
                      w.fillRate < 70 && 'text-emerald-600 dark:text-emerald-400',
                      w.fillRate >= 70 && w.fillRate < 90 && 'text-amber-600 dark:text-amber-400',
                      w.fillRate >= 90 && 'text-rose-600 dark:text-rose-400'
                    )}
                  >
                    {Math.round(w.fillRate)}%
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-4 px-3 font-semibold">Week</th>
                  <th className="text-center py-4 px-3 font-semibold">Capacity</th>
                  <th className="text-center py-4 px-3 font-semibold">Filled</th>
                  <th className="text-center py-4 px-3 font-semibold">Available</th>
                  <th className="text-center py-4 px-3 font-semibold">Fill Rate</th>
                  <th className="text-center py-4 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.next12WeeksData.map((w, i) => (
                  <tr
                    key={w.id}
                    onClick={() => onNavigateToCalendarWeek?.(w.week_start)}
                    className={cn(
                      'border-b transition-colors hover:bg-muted/50',
                      onNavigateToCalendarWeek && 'cursor-pointer',
                      i % 2 === 0 && 'bg-muted/20'
                    )}
                  >
                    <td className="py-4 px-3 font-medium">
                      {formatDateRange(w.week_start, w.week_end)}
                    </td>
                    <td className="text-center py-4 px-3">{w.capacity}</td>
                    <td className="text-center py-4 px-3 font-medium">{w.filledSpots}</td>
                    <td className="text-center py-4 px-3">{w.availableSpots}</td>
                    <td className="text-center py-4 px-3">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              w.fillRate < 70 && 'bg-emerald-500',
                              w.fillRate >= 70 && w.fillRate < 90 && 'bg-amber-500',
                              w.fillRate >= 90 && 'bg-rose-500'
                            )}
                            style={{ width: `${Math.min(w.fillRate, 100)}%` }}
                          />
                        </div>
                        <span
                          className={cn(
                            'font-semibold min-w-[3ch]',
                            w.fillRate < 70 && 'text-emerald-600 dark:text-emerald-400',
                            w.fillRate >= 70 && w.fillRate < 90 && 'text-amber-600 dark:text-amber-400',
                            w.fillRate >= 90 && 'text-rose-600 dark:text-rose-400'
                          )}
                        >
                          {Math.round(w.fillRate)}%
                        </span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-3">
                      {w.hasHardBlock ? (
                        <Badge variant="secondary" className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                          <Ban className="h-3 w-3 mr-1" />
                          Blocked
                        </Badge>
                      ) : w.fillRate > 100 ? (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                          Over Capacity
                        </Badge>
                      ) : w.fillRate === 100 ? (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                          Full
                        </Badge>
                      ) : w.fillRate >= 90 ? (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                          Near Full
                        </Badge>
                      ) : w.fillRate >= 70 ? (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                          Filling
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                          Available
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}
